import { reference, type DocsOperation } from '../generated/reference';
import {
  docsSessionSchema,
  type DocsSession,
  type RememberedValue,
} from '../schemas/docs-storage-schema';

import { DOCS_SESSION_KEY, parseJson, readItem, writeItem } from './docs-storage';
import type { TryItRequest, TryItResponse } from './try-it-client';

/** Response fields worth carrying into the next request's path (plan 9.2). */
export const REMEMBERED_FIELDS = ['token', 'id', 'handle', 'shareToken', 'recoveryToken'] as const;

const REMEMBERED_PER_NAME = 10;

export const emptyDocsSession = (): DocsSession => ({
  customer: null,
  idempotency: { last: null },
  remembered: {},
  paymentConfirmed: false,
});

export function parseDocsSession(raw: string | null): DocsSession {
  return raw ? docsSessionSchema.parse(parseJson(raw)) : emptyDocsSession();
}

/** Read-modify-write against storage, so two updates in one tick both land. */
export function updateDocsSession(update: (session: DocsSession) => DocsSession): void {
  const next = update(parseDocsSession(readItem('session', DOCS_SESSION_KEY)));
  writeItem('session', DOCS_SESSION_KEY, JSON.stringify(next));
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Any response with an `accessToken` and a `refreshToken` signs the customer in for this tab:
 * login, register, magic link, Google and refresh (which rotates the pair). The email comes from
 * `customer.email` when the response carries it, else the previous one is kept.
 */
export function captureCustomer(session: DocsSession, body: unknown): DocsSession {
  if (!isRecord(body)) return session;
  const { accessToken, refreshToken } = body;
  if (typeof accessToken !== 'string' || typeof refreshToken !== 'string') return session;
  const customer = isRecord(body.customer) ? body.customer : null;
  const email = typeof customer?.email === 'string' ? customer.email : null;
  return {
    ...session,
    customer: { accessToken, refreshToken, email: email ?? session.customer?.email ?? null },
  };
}

/**
 * Remembers the top-level `token`, `id`, `handle`, `shareToken` and `recoveryToken` of a body, or
 * of its first `items` entry for lists, newest first, ten per name.
 */
export function rememberValues(session: DocsSession, body: unknown, from: string): DocsSession {
  const first: unknown = isRecord(body) && Array.isArray(body.items) ? body.items[0] : undefined;
  const source = isRecord(first) ? first : isRecord(body) ? body : null;
  if (!source) return session;
  const remembered = { ...session.remembered };
  for (const name of REMEMBERED_FIELDS) {
    const value = source[name];
    if (typeof value !== 'string' || value === '') continue;
    const previous = (remembered[name] ?? []).filter((entry) => entry.value !== value);
    remembered[name] = [{ value, from }, ...previous].slice(0, REMEMBERED_PER_NAME);
  }
  return { ...session, remembered };
}

/** `/storefront/v1/carts/{token}/lines` → `/storefront/v1/carts` for the param `token`. */
function pathBefore(path: string, name: string): string | null {
  const at = path.indexOf(`{${name}}`);
  return at < 0 ? null : path.slice(0, at).replace(/\/$/, '');
}

/**
 * The remembered value a path parameter starts with. A value from an operation on the same
 * resource wins (a cart `token` for `/carts/{token}`, not the checkout's), else the newest one.
 */
export function pickRemembered(
  session: DocsSession,
  operation: Pick<DocsOperation, 'path'>,
  name: string,
): RememberedValue | undefined {
  const entries = session.remembered[name] ?? [];
  const prefix = pathBefore(operation.path, name);
  const sameResource =
    prefix === null
      ? undefined
      : entries.find((entry) => reference.operations[entry.from]?.path.startsWith(prefix));
  return sameResource ?? entries[0];
}

/**
 * What a response teaches the session: the Idempotency-Key it was sent with (for "Reuse last"),
 * and on success the customer tokens (logout clears them) and the remembered values.
 */
export function applyTryItResponse(
  session: DocsSession,
  operation: Pick<DocsOperation, 'id'>,
  request: TryItRequest,
  response: TryItResponse,
): DocsSession {
  const sentKey = request.headers['Idempotency-Key'];
  let next = sentKey ? { ...session, idempotency: { last: sentKey } } : session;
  if (response.status < 200 || response.status >= 300) return next;
  if (operation.id === 'logoutCustomer') return { ...next, customer: null };
  if (response.body.kind !== 'json') return next;
  next = captureCustomer(next, response.body.value);
  return rememberValues(next, response.body.value, operation.id);
}

/**
 * Recorded bodies carry `<customerAccessToken>` / `<customerRefreshToken>` where the recorder
 * redacted the session (logout, refresh); the signed-in customer's tokens replace them.
 */
export function fillSessionPlaceholders(text: string, session: DocsSession): string {
  const { customer } = session;
  if (!customer) return text;
  return text
    .replaceAll('<customerAccessToken>', customer.accessToken)
    .replaceAll('<customerRefreshToken>', customer.refreshToken);
}
